import React, { useRef, useState } from "react";
import * as Yup from "yup";
import Image from "next/image";
import dynamic from "next/dynamic";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { useRouter } from "next/navigation";
import { ReCAPTCHA } from "@/lib/vendor";
import { Formik, Form, Field, ErrorMessage } from "formik";

import { PHONE_REGEX } from "@/utils/common";
import apiService from "@/common/services/apiService";
import { hideFormLoader, showFormLoader } from "@/store/loaderSlice";

// Dynamic imports
const FormInput = dynamic(() => import("./FormInput"));
const Button = dynamic(() => import("@/Components/Button"));

interface QuoteFormValues {
  email: string;
  budget: string;
  contact_on: string;
  country_name: string;
  full_name: string;
  file: File | null;
  service_type: string[];
  ndaSigned: boolean;
  confidential: boolean;
  project_summary: string;
}

const ErrorMessageComponent = (props: any) => (
  <div className="text-red-500 font-medium text-xs mt-2" role="alert">
    {props.children}
  </div>
);

interface QuoteFormProps {
  data?: any 
}

const QuoteForm: React.FC<QuoteFormProps> = ({ data  }) => {
  const router = useRouter();
  const dispatch = useDispatch();
  const reCaptchaRef = useRef<ReCAPTCHA>(null);
  const [isLoading, setIsLoading] = useState(false);
  const sitekey = process.env.NEXT_PUBLIC_RECAPTCHA_V3;
  const [value, setBudgetValue] = useState([5000, 150000]);
  const [selectedFiles, setSelectedFiles] = useState<any[]>([]);

  const initialValues: QuoteFormValues = {
    email: "",
    file: null,
    budget: "",
    full_name: "",
    contact_on: "",
    ndaSigned: true,
    country_name: "",
    confidential: true,
    project_summary: "",
    service_type: ["Web Development"],
  };

  const validationSchema = Yup.object({
    full_name: Yup.string().trim().required("Full name is required."),
    email: Yup.string()
      .trim()
      .email("Email is not valid.")
      .max(100, "Email is too long")
      .required("Email is required.")
      .matches(/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/, "Invalid email format"),
    contact_on: Yup.string()
      .nullable()
      .required("Phone is required")
      .matches(PHONE_REGEX, "Invalid phone number")
      .matches(/^[0-9]{10,15}$/, "Phone number must be between 10-15 digits"),
    country_name: Yup.string().required("Country is required."),
    project_summary: Yup.string().required("Project description is required"),
  });

  // const handleBudget = (val: string, setFieldValue: any) => {
  //   setBudget(val);
  //   setFieldValue("budget", val);
  //   const budget = val.replaceAll("$", "").replaceAll("K", "000");
  //   setBudgetValue(budget.split("-").map((item: string) => Number(item)));
  // };

  const validateCaptchaV3 = () => {
    return new Promise((resolve) => {
      reCaptchaRef?.current?.executeAsync()?.then(async (value: unknown) => {
        resolve(value);
        // const captchaRes = await axios.post(`/api/validateCaptchaV3`, {token: value});
        // if (captchaRes.status === 200 && captchaRes.data.success) resolve(true); else reject(false);
      });
    });
  };

  const handleSubmit = async (
    values: QuoteFormValues,
    { resetForm, setSubmitting }: any
  ) => {
    try {
      setIsLoading(true);
      const isHuman = await validateCaptchaV3();
      if (!isHuman) return false;
      dispatch(showFormLoader());
      const payload = { ...values } as Record<string, any>;
      data.service_type = data?.services?.toString() ?? "";
      const form_data = new FormData();
      for (const key in payload) {
        if (key === "file") {
          selectedFiles.forEach((file) => {
            form_data.append("project_files", file);
          });
        } else if (payload[key])
          form_data.append(key, payload[key]?.toString());
      }
      form_data.append("page_link", window.location.href);
      form_data.append("captcha_token_v3", isHuman as string);
      const page_referer = sessionStorage.getItem("rfp-referer");
      form_data.append("page_title", document.title ?? "Trootech");
      form_data.append("project_budget_min", value[0].toString() ?? "-");
      form_data.append("project_budget_max", value[1].toString() ?? "-");
      if (!!page_referer) {
        form_data.append("page_referer", page_referer);
        sessionStorage.removeItem("rfp-referer");
      }
      const lst: any = await apiService.multipart_post("proposal", form_data);
      if (lst.data.success) {
        dispatch(hideFormLoader());
        setBudgetValue([500, 150000]);
        setSelectedFiles([]);
        resetForm();
        setSubmitting(false);
        reCaptchaRef?.current?.reset();
        router.push("/thank-you");
      } else toast.error(lst?.data?.message ?? "Something went wrong");
    } catch (error) {
      console.log("handleSubmit ~ error:", error);
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 5000);
    }
  };

  return (
    <div className="sm:bg-[url(/images/Quote-bg.webp)] bg-[url(/images/bg-mobile-quote.png)] bg-no-repeat bg-cover bg-center">
      <Formik
        onSubmit={handleSubmit}
        initialValues={initialValues}
        validationSchema={validationSchema}
      >
        {(
          {
            /* setFieldValue, values */
          }
        ) => (
          <Form
            className="xl:pt-12 xl:px-12 xl:pb-6 md:px-8 md:pt-8 md:pb-[50px] px-4 pt-6 pb-6 h-full items-stretch"
            aria-label="Request for Proposal Form"
          >
            <h5 className="text-heading-5 font-bold text-primary-text text-center ">
              Book Your CRM Management Consultation Today!
            </h5>

            <div className="grid lg:grid-cols-1 gap-4 md:mt-6 mt-4 h-full items-stretch">
              <div className="grid lg:grid-cols-2 gap-4 h-full items-stretch">
                <FormInput name="full_name" placeholder="Full name" required />
                <FormInput
                  name="email"
                  type="email"
                  placeholder="Email"
                  required
                />
                <FormInput
                  name="contact_on"
                  type="tel"
                  placeholder="Phone number"
                  required
                />
                <FormInput name="country_name" placeholder="Country" required />

                <div className="textarea input-group sm:col-span-2 mb-0 ">
                  <Field
                    as="textarea"
                    name="project_summary"
                    id="project_summary"
                    className="input h-28 min-h-[120px]"
                    placeholder=" "
                    aria-label="Project description"
                    aria-describedby="project_summary-error"
                    aria-required="true"
                  />
                  <label
                    htmlFor="project_summary"
                    className="text-subtitle-5 font-regular"
                  >
                    Project description*
                  </label>
                  <ErrorMessage
                    name="project_summary"
                    component={ErrorMessageComponent}
                    id="project_summary-error"
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center md:justify-between justify-end md:flex-nowrap flex-wrap gap-6 mt-6">
              <div className="flex items-center gap-8 md:w-auto w-full">
                <div className="flex items-center gap-3">
                  <Image
                    src="/images/confidential.svg"
                    alt="confidential"
                    width={18}
                    height={18}
                  />
                  <span className="text-primary-text text-subtitle-4 font-regular">
                    100% confidential
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <Image
                    src="/images/confidential.svg"
                    alt="confidential"
                    width={18}
                    height={18}
                  />
                  <span className="text-primary-text text-subtitle-4 font-regular">
                    We sign NDA
                  </span>
                </div>
              </div>
              <Button
                variant="small"
                className="my-0"
                label={data?.button_text || "Submit"}
                type="submit"
                loading={isLoading}
              />
            </div>
            {sitekey && (
              <ReCAPTCHA
                style={{ display: "inline" }}
                theme="dark"
                size="invisible"
                ref={reCaptchaRef}
                sitekey={sitekey}
              />
            )}
          </Form>
        )}
      </Formik>
    </div>
  );
};
export default QuoteForm;